import { adminApi } from './admin-api';
import type { Incident, Maintenance, Announcement, EmailCampaign, ApiResponse, PaginatedResponse, AdminFilters } from '@/types/admin.types';
import type { IncidentFormData, MaintenanceFormData, AnnouncementFormData, EmailCampaignFormData, IncidentUpdateFormData } from '@/schemas/communication.schema';

export const adminCommunicationsService = {
  // Incidents
  getIncidents: async (filters: AdminFilters = {}): Promise<ApiResponse<PaginatedResponse<Incident>>> => {
    const params = new URLSearchParams();
    if (filters.page) params.set('page', String(filters.page));
    if (filters.limit) params.set('limit', String(filters.limit));
    const response = await adminApi.get(`/admin/communications/incidents?${params.toString()}`);
    return response.data;
  },

  createIncident: async (data: IncidentFormData): Promise<ApiResponse<Incident>> => {
    const response = await adminApi.post('/admin/communications/incidents', data);
    return response.data;
  },

  updateIncident: async (uuid: string, data: IncidentUpdateFormData): Promise<ApiResponse<Incident>> => {
    const response = await adminApi.patch(`/admin/communications/incidents/${uuid}`, data);
    return response.data;
  },

  resolveIncident: async (uuid: string): Promise<ApiResponse<{ message: string }>> => {
    const response = await adminApi.post(`/admin/communications/incidents/${uuid}/resolve`);
    return response.data;
  },

  // Maintenances
  getMaintenances: async (filters: AdminFilters = {}): Promise<ApiResponse<PaginatedResponse<Maintenance>>> => {
    const params = new URLSearchParams();
    if (filters.page) params.set('page', String(filters.page));
    if (filters.limit) params.set('limit', String(filters.limit));
    const response = await adminApi.get(`/admin/communications/maintenances?${params.toString()}`);
    return response.data;
  },

  createMaintenance: async (data: MaintenanceFormData): Promise<ApiResponse<Maintenance>> => {
    const response = await adminApi.post('/admin/communications/maintenances', data);
    return response.data;
  },

  updateMaintenance: async (uuid: string, data: Partial<MaintenanceFormData>): Promise<ApiResponse<Maintenance>> => {
    const response = await adminApi.patch(`/admin/communications/maintenances/${uuid}`, data);
    return response.data;
  },

  cancelMaintenance: async (uuid: string): Promise<ApiResponse<{ message: string }>> => {
    const response = await adminApi.post(`/admin/communications/maintenances/${uuid}/cancel`);
    return response.data;
  },

  // Announcements
  getAnnouncements: async (filters: AdminFilters = {}): Promise<ApiResponse<PaginatedResponse<Announcement>>> => {
    const params = new URLSearchParams();
    if (filters.page) params.set('page', String(filters.page));
    if (filters.limit) params.set('limit', String(filters.limit));
    const response = await adminApi.get(`/admin/communications/announcements?${params.toString()}`);
    return response.data;
  },

  createAnnouncement: async (data: AnnouncementFormData): Promise<ApiResponse<Announcement>> => {
    const response = await adminApi.post('/admin/communications/announcements', data);
    return response.data;
  },

  updateAnnouncement: async (uuid: string, data: Partial<AnnouncementFormData>): Promise<ApiResponse<Announcement>> => {
    const response = await adminApi.patch(`/admin/communications/announcements/${uuid}`, data);
    return response.data;
  },

  deleteAnnouncement: async (uuid: string): Promise<ApiResponse<{ message: string }>> => {
    const response = await adminApi.delete(`/admin/communications/announcements/${uuid}`);
    return response.data;
  },

  // Email Campaigns
  getCampaigns: async (filters: AdminFilters = {}): Promise<ApiResponse<PaginatedResponse<EmailCampaign>>> => {
    const params = new URLSearchParams();
    if (filters.page) params.set('page', String(filters.page));
    if (filters.limit) params.set('limit', String(filters.limit));
    const response = await adminApi.get(`/admin/communications/campaigns?${params.toString()}`);
    return response.data;
  },

  createCampaign: async (data: EmailCampaignFormData): Promise<ApiResponse<EmailCampaign>> => {
    const response = await adminApi.post('/admin/communications/campaigns', data);
    return response.data;
  },

  sendCampaign: async (uuid: string): Promise<ApiResponse<{ message: string }>> => {
    const response = await adminApi.post(`/admin/communications/campaigns/${uuid}/send`);
    return response.data;
  },

  getCounts: async () => {
    const response = await adminApi.get('/admin/communications/counts');
    return response.data;
  },
};
